/**
 * Обзор (§51).
 *
 * Операционный минимум для дежурного, а не аналитический дашборд (§80):
 * что в работе, что падает, во что это обходится.
 */
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin';
import { formatNumber } from '../../i18n/format';
import { useTranslation } from 'react-i18next';

const cents = (value: number): string => `$${(value / 100).toFixed(2)}`;

export function AdminOverviewPage() {
  const { t } = useTranslation();
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: adminApi.overview,
    refetchInterval: 15_000,
  });

  const data = overview.data;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label={t('admin.metric.active')} value={data?.activeJobs} />
        <Metric label={t('admin.metric.queued')} value={data?.queuedJobs} />
        <Metric
          label={t('admin.metric.failedDay')}
          value={data ? `${data.failedLastDay} (${data.failureRatePercent}%)` : undefined}
          // Доля важнее числа: 10 падений на 20 задач и на 2000 — разные ситуации.
          tone={data && data.failureRatePercent > 20 ? 'bad' : undefined}
        />
        <Metric label={t('admin.metric.doneDay')} value={data?.completedLastDay} />
        <Metric label={t('admin.metric.users')} value={data?.users} />
        <Metric label={t('admin.metric.newUsers')} value={data?.newUsersLastDay} />
        <Metric
          label={t('admin.metric.providersDown')}
          value={data?.providersDown}
          tone={data && data.providersDown > 0 ? 'bad' : undefined}
        />
        <Metric
          label={t('admin.metric.walletMismatch')}
          value={data?.walletMismatches}
          tone={data && data.walletMismatches > 0 ? 'bad' : undefined}
        />
      </div>

      {/* Две половины маржи рядом: себестоимость против списанного (§35). */}
      <section className="rounded-card border border-line bg-surface p-4">
        <p className="mb-3 text-meta text-content-secondary">{t('admin.economy')}</p>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-meta text-content-muted">{t('admin.providerCost')}</p>
            <p className="text-card-title text-content">
              {data ? cents(data.providerCostCentsLastDay) : '—'}
            </p>
          </div>
          <div>
            <p className="text-meta text-content-muted">{t('admin.unitsSpent')}</p>
            <p className="text-card-title text-content">
              {data ? formatNumber(data.unitsSpentLastDay) : '—'}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-card border border-line bg-surface p-4">
        <p className="mb-3 text-meta text-content-secondary">{t('admin.load')}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-meta">
            <thead>
              <tr className="text-left text-content-secondary">
                <th className="py-1 font-medium">{t('admin.kind')}</th>
                <th className="py-1 text-right font-medium">{t('admin.metric.active')}</th>
                <th className="py-1 text-right font-medium">{t('admin.failures')}</th>
                <th className="py-1 text-right font-medium">{t('admin.avgTime')}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.queues ?? []).map((queue) => (
                <tr key={queue.kind} className="border-t border-line">
                  <td className="py-1.5">{queue.kind}</td>
                  <td className="py-1.5 text-right tabular-nums">{queue.pending}</td>
                  <td className="py-1.5 text-right tabular-nums">{queue.failedLastDay}</td>
                  <td className="py-1.5 text-right tabular-nums text-content-secondary">
                    {queue.avgSeconds !== null ? t('admin.secondsShort', { value: queue.avgSeconds }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string | undefined;
  tone?: 'bad';
}) {
  return (
    <div className="rounded-card border border-line bg-surface px-4 py-3">
      <p className="text-meta text-content-muted">{label}</p>
      <p className={`text-card-title ${tone === 'bad' ? 'text-error' : 'text-content'}`}>
        {value ?? '—'}
      </p>
    </div>
  );
}
